import * as THREE from 'three';
import { sliceMesh, SliceResult, get_vertexpos } from './intersection';

export interface WaistResult {
  /** World-space position of the anchor on the axis */
  anchorPos: THREE.Vector3;
  /** Closest mesh vertex index used as anchor */
  anchorIndex: number;
  /** Slice result at this anchor */
  slice: SliceResult;
}

export interface WaistSample {
  /** World-space position tested */
  anchorPos: THREE.Vector3;
  /** Mesh vertex index used as anchor */
  anchorIndex: number;
  /** Raw slice circumference (cm) */
  length: number;
  /** Convex hull circumference (cm) */
  hullLength: number;
}

export interface WaistSearchResult {
  modelHeight: number; // optional, if model height is calculated
  /** The best/narrowest waist sample */
  best: WaistResult;
  /** All tested samples for debugging */
  samples: WaistSample[];
  scale?: number;
}

/**
 * Scans between two mesh points (lowerIdx -> upperIdx) to find the narrowest slice,
 * choosing by convex hull perimeter, and returns all samples for debugging.
 * @param mesh - The body mesh to slice.
 * @param lowerIdx - Vertex index at the lower bound (e.g., hip bottom).
 * @param upperIdx - Vertex index at the upper bound (e.g., waist top).
 * @param normalSet1 - Array of indices defining first normal endpoint(s).
 * @param normalSet2 - Array of indices defining second normal endpoint(s).
 * @param steps - Number of samples between lower and upper (default=20).
 * @returns Object containing the best waist and all samples.
 */
export function findNarrowestWaist(
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>,
  lowerIdx: number,
  upperIdx: number,
  normalSet1: number[],
  normalSet2: number[],
  steps: number = 20
): WaistSearchResult {
  mesh.updateMatrixWorld();
  const geom = mesh.geometry;
  const posAttr = geom.attributes.position as THREE.BufferAttribute;

  // Resolve world-space endpoints for the axis
  const resolveIndex = (i: number) => geom.index ? geom.index.getX(i) : i;
  const lowerPos = get_vertexpos(lowerIdx, geom)
    .applyMatrix4(mesh.matrixWorld);
  const upperPos = get_vertexpos(upperIdx, geom)
    .applyMatrix4(mesh.matrixWorld);
  const modelHeight= lowerPos.distanceTo(upperPos) * 100; // convert to cm
  let bestResult: WaistResult | null = null;
  let minHull = Infinity;
  const samples: WaistSample[] = [];

  // Sample along the axis
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const anchorPos = lowerPos.clone().lerp(upperPos, t);
    // snap to nearest mesh vertex
    let bestIdx = 0;
    let minD2 = Infinity;
    //const tmp = new THREE.Vector3();
    for (let j = 0; j < posAttr.count; j++) {
      const idx = resolveIndex(j);
      const tmp = get_vertexpos(j, geom).applyMatrix4(mesh.matrixWorld);
      const d2 = tmp.distanceToSquared(anchorPos);
      if (d2 < minD2) {
        minD2 = d2;
        bestIdx = j;
      }
    }
    const anchorIndex = bestIdx;
    // checkif achorIndex has already been used
    if (samples.some(sample => sample.anchorIndex === anchorIndex)) {
      continue;
    }
    // perform slice and record
    const [slice] = sliceMesh(mesh, anchorIndex, normalSet1, normalSet2);
    samples.push({ anchorPos, anchorIndex, length: slice.length, hullLength: slice.hullLength });

    // choose by hullLength
    if (slice.hullLength < minHull) {
      minHull = slice.hullLength;
      bestResult = { anchorPos, anchorIndex, slice };
    }
  }

  if (!bestResult) {
    throw new Error('No valid slices found between given bounds.');
  }

  return { best: bestResult, samples, modelHeight: modelHeight};
}
